import { classes } from '@theme/constants';
import { getRefs, morph, parseHtml } from '@theme/dom';
import { text } from '@theme/fetch';
import { debounce } from '@theme/helpers';
import { HTMLCustomElement, HTMLShopifySectionElement } from '@theme/html-custom-element';

class SearchOverlay extends HTMLShopifySectionElement {
  requiredAttributes = ['section-id'];

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('input', this.refs.input, debounce(this.handleInputEvent.bind(this), 150, true), null, true);
    this.on('open', this.refs.toggleElement, this.handleToggleElementOpen.bind(this));
  }

  /**
   * Focuses the search input on toggle element open.
   */
  handleToggleElementOpen() {
    requestAnimationFrame(() => {
      this.refs.input.focus();
      this.refs.input.selectionStart = this.refs.input.selectionEnd = this.refs.input.value.length;
    });
  }

  /**
   * Handles the query input event.
   * @param {InputEvent} event
   */
  async handleInputEvent(event) {
    const value = event ? event.target.value : this.refs.input.value;

    if (value === '') {
      /**
       * If the search query is empty, fetch the default section content.
       */
      this.update(await text(fetch(`${theme.routes.root_url}?section_id=${this.getAttribute('section-id')}`)));
      return;
    }

    this.search(value).then((response) => {
      this.update(response);
    });
  }

  /**
   * Queries the Predictive Search API.
   * @param {string} query - The search query.
   * @returns {Promise}
   */
  search(query) {
    return text(
      fetch(
        `${theme.routes.predictive_search_url}?q=${query}&resources[limit_scope]=each&section_id=${this.getAttribute('section-id')}`,
      ),
    );
  }

  /**
   * Updates the elements.
   * @param {string} response - The Section Rendering API response.
   */
  async update(response) {
    const element = parseHtml(response, this.tagName.toLowerCase());
    const refs = getRefs(element);

    if (refs.panel) {
      await morph(this.refs.panel, refs.panel, true, (from) => !from.hasAttribute('data-exclude'));

      if (window.StampedFn && typeof window.StampedFn.loadBadges === 'function') {
        window.StampedFn.loadBadges();
      }
    }
  }
}

class SearchOverlayLinks extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (this.refs.link) {
      this.on('mouseenter', this.refs.link.all, (event) => {
        if (this.refs.image) {
          for (const element of this.refs.image.all) {
            element.classList.toggle(classes.active, event.target.dataset.index0 === element.dataset.index0);
          }
        }
      });
    }
  }
}

if (!customElements.get('search-overlay')) customElements.define('search-overlay', SearchOverlay);
if (!customElements.get('search-overlay-links')) customElements.define('search-overlay-links', SearchOverlayLinks);
